'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { ProductDisplayData as Product } from '@eliadmin/core/src/api/products/types/product-types';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import {
  getProductFilterVariants,
  getProductImage,
  getProductUrl,
} from '@eliadmin/core/src/utils/product-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { AddToFavoritesButton } from '../add-to-favorites-button';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
export type ProductCardProps = {
  product: Product;
  className?: {
    main?: string;
  };
};

export function ProductCard({ product, className }: ProductCardProps) {
  //   const isAuth = useGlobalStore(state => state.config.isAuth);
  const { t } = useTranslations();
  const templateSettings = useGlobalStore(s => s.templateSettings);
  const isWholesaler = useCustomerStore(s => s.me?.customer.is_wholesaler);
  const locale = useLocaleStore(s => s.currentLocale);
  // const api = useGlobalStore(state => state.config.api);
  const filter = useCategoryStore(state => state.filter);

  const filterVariants = getProductFilterVariants(product, filter);
  const image = getProductImage(templateSettings, product, filterVariants);
  // This catalogue card has no variant selector. The old per-card provider always
  // started with [] and no descendant changed it; keep exactly that URL contract.
  const productUrl = getProductUrl(product, []);
  const priceDisplay = getProductPriceDisplay(product, {
    isWholesaler,
    priceWithoutVatFirst: templateSettings.product_detail?.price_without_vat_first?.enabled,
  });
  const generalLayout = useGlobalStore(s => s.generalLayout);

  const saleName = locale === 'sk' ? 'Výpredaj' : 'Sale';

  const hasManufacturer = product?.manufacturers && product.manufacturers.length > 0;

  return (
    <div className="group col-span-1 flex w-full flex-col items-start justify-start gap-0 transition-all duration-300 ease-in-out hover:-translate-y-4">
      <div className="relative h-[460px] w-full sm:h-[350px] lg:h-[400px] xl:h-[480px]">
        {/* AddToFavoritesButton is moved outside the Link component */}
        <div className="absolute right-4 top-4 z-[5] h-12 w-12">
          <AddToFavoritesButton
            productId={product.id}
            slug={product.slug}
            isFavorite={product.is_favorite ?? false}
            className={{ container: 'aspect-square h-12 w-12 bg-white p-0' }}
          />
        </div>

        <Link
          prefetchOnHover
          prefetch={false}
          href={productUrl}
          className="absolute inset-0 z-0 h-full w-full"
        >
          <Image
            src={image?.url ?? '/images/placeholder.png'}
            alt={resolveImageAlt(image, product?.name)}
            fill
            style={{ objectFit: 'cover', objectPosition: 'center' }}
            className="absolute inset-0 z-0 h-full w-full"
            sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, 33vw"
          />
        </Link>
      </div>

      <Link
        prefetchOnHover
        prefetch={false}
        href={productUrl}
        className="flex w-full flex-col items-start justify-start gap-2 py-4"
      >
        <h2
          data-ea-visual-tag="h3"
          className="line-clamp-1 text-2xl font-medium uppercase text-foreground transition-all group-hover:text-primary"
        >
          {product.name}
          {!!hasManufacturer && ` | ${product.manufacturers![0].name}`}
        </h2>

        {product.categories && product.categories[0]?.name === saleName && (
          <div className="flex w-full flex-col items-start gap-1">
            {priceDisplay.priceOnRequest ? (
              <strong className="text-primary">{t('text2278')}</strong>
            ) : (
              <>
                {priceDisplay.showMocVocPrices && priceDisplay.mocPrice != null && (
                  <span className="text-sm text-[#A7A7A7]">
                    {t('text1926')}{' '}
                    {formatPrice(
                      priceDisplay.mocPrice,
                      generalLayout.currency,
                      generalLayout.currency_settings,
                    )}
                  </span>
                )}
                <div className="flex items-baseline gap-2">
                  <p className="text-2xl font-normal text-foreground">
                    {formatPrice(
                      priceDisplay.current,
                      generalLayout.currency,
                      generalLayout.currency_settings,
                    )}
                  </p>
                  {priceDisplay.showPreviousPrice && (
                    <p className="text-lg text-[#A7A7A7] line-through">
                      {formatPrice(
                        priceDisplay.beforeDiscount,
                        generalLayout.currency,
                        generalLayout.currency_settings,
                      )}
                    </p>
                  )}
                </div>
                {priceDisplay.secondary != null && (
                  <span className="text-sm text-[#A7A7A7]">
                    {priceDisplay.showMocVocPrices ? `${t('text1925')} ` : ''}
                    {formatPrice(
                      priceDisplay.secondary,
                      generalLayout.currency,
                      generalLayout.currency_settings,
                    )}{' '}
                    {priceDisplay.secondaryIncludesVat ? t('text1122') : t('text443')}
                  </span>
                )}
              </>
            )}
          </div>
        )}
      </Link>
    </div>
  );
}
